import { useEffect, useState } from 'react'
import { Effect, pipe } from 'effect'
import type { Race } from 'src/types/domain/Race'
import { getRacesByGameId } from 'src/services/supabase/queries'
import { loadRaceHistory, RACE_HISTORY_TTL_MS } from 'src/utils/raceHistoryCache'
import { HISTORY } from 'src/config'

/**
 * One-shot deep read of a game's recent rounds, for the past-races panel.
 *
 * Deliberately NOT wired through the game store or `getGameById`:
 *  • `getGameById` re-fires on mount and on every visibilitychange/focus/online
 *    (useSyncWithRetry), so widening ITS limit would multiply the payload on
 *    the hottest path in the widget. This runs once, when the panel mounts.
 *  • `setGame` replaces `raceIds` wholesale on each of those re-syncs, so
 *    anything written to the store gets truncated back to ~10. Holding the
 *    window in local state is what keeps the sample size stable — and it is
 *    what makes two players computing the same-labelled percentage agree.
 *
 * A failure is not surfaced as an error: the panel falls back to the store's
 * ~10 rounds, which is exactly today's behaviour.
 */
export type DeepRaceHistory = Readonly<{
  races: Race[]
  /** True until the FIRST read for this game resolves. Callers show a
   *  skeleton rather than rendering stats over the store's ~10 rounds and
   *  then reflowing when the real window lands. Refreshes do not set it. */
  isLoading: boolean
}>

export const useDeepRaceHistory = (gameId: string | undefined): DeepRaceHistory => {
  const [races, setRaces] = useState<Race[]>([])
  const [isLoading, setIsLoading] = useState(Boolean(gameId))

  // Switching games must not leave the previous game's window on screen while
  // the new read is in flight.
  useEffect(() => {
    setRaces([])
    setIsLoading(Boolean(gameId))
  }, [gameId])

  useEffect(() => {
    if (!gameId) return undefined

    let cancelled = false

    const read = () => {
      void loadRaceHistory(gameId, Date.now(), () =>
        pipe(
          getRacesByGameId({
            gameId,
            limit: HISTORY.DEEP_ROUNDS,
            orderBy: 'created_at',
            order: 'desc'
          }),
          Effect.orElseSucceed(() => [] as Race[]),
          Effect.runPromise
        )
      ).then((fetched) => {
        if (cancelled) return
        if (fetched.length > 0) setRaces(fetched)
        // Cleared even on a failed read ([]), so a Supabase outage falls back
        // to the store's rounds instead of showing a skeleton forever.
        setIsLoading(false)
      })
    }

    read()

    // A panel left open would otherwise hold a FROZEN window while the store
    // slides forward: the store keeps only the newest ~10 rounds and re-seeds
    // itself on every refocus, so once more than 10 rounds have elapsed since
    // the snapshot, the rounds in between belong to neither source and the
    // merged history develops a hole. Re-reading on the cache's own TTL bounds
    // that gap to a round or two. Only ever runs while the panel is open.
    const interval = setInterval(read, RACE_HISTORY_TTL_MS)

    return () => {
      cancelled = true
      clearInterval(interval)
    }
  }, [gameId])

  return { races, isLoading }
}
